import { useEffect } from 'react';

type Props = {
  title: string;
  description: string;
  canonical?: string;
  jsonLd?: Record<string, unknown>;
  ogImage?: string;     // full URL, overrides the index.html default
};

/**
 * Lightweight SEO head manager. Avoids react-helmet for now — the surface
 * (title, description, canonical, optional JSON-LD) is small enough to
 * manipulate directly.
 *
 * For pre-rendered SSR/SSG later, swap this for a static <Head> emitter.
 */
export function SEO({ title, description, canonical, jsonLd, ogImage }: Props) {
  useEffect(() => {
    // Netlify serves prerendered pages at their trailing-slash URL and 301s the
    // non-slash form. Canonical + og:url must point at the 200 URL, not the
    // redirect — so normalise the canonical to always end in a slash.
    let canon = canonical;
    if (canon) {
      try {
        const u = new URL(canon);
        if (!u.pathname.endsWith('/')) u.pathname += '/';
        canon = u.toString();
      } catch { /* non-absolute URL — leave as provided */ }
    }
    document.title = title;
    upsertMeta('name', 'description', description);
    if (canon) upsertLink('canonical', canon);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    if (canon) upsertMeta('property', 'og:url', canon);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    if (ogImage) {
      upsertMeta('property', 'og:image', ogImage);
      upsertMeta('name', 'twitter:image', ogImage);
    }

    const id = 'aces-jsonld';
    document.getElementById(id)?.remove();
    if (jsonLd) {
      const s = document.createElement('script');
      s.id = id;
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [title, description, canonical, jsonLd, ogImage]);
  return null;
}

function upsertMeta(attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = value;
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}
